<template>
  <RevLoadingScreen
    v-if="!offer"
    class="my-auto"
    text="Your offer is loading"
  />
  <div v-else class="flex flex-col justify-center gap-24 md:gap-32">
    <div
      class="flex max-w-full grow flex-col gap-24 p-24 pb-0 md:mx-auto md:w-[800px] md:gap-32 lg:basis-full"
    >
      <div
        class="border-static-default-low relative flex items-center justify-center border-b px-40 pb-20 pt-8"
      >
        <h1 class="body-1">{{ i18n(translations.title) }}</h1>
        <RevButtonIcon
          aria-label="Close"
          class="absolute right-0"
          :icon="IconCross"
          variant="secondary"
          @click="declineOffer"
        />
      </div>

      <div class="flex flex-col items-center">
        <span v-if="offer.tier === 'low'" class="heading-2">
          {{ i18n(translations.priceOffer) }}
        </span>
        <span v-else class="heading-2">
          {{ i18n(translations.congratsOffer) }}
        </span>

        <AnimatedPrice
          :animation-delay="0"
          class="mt-8"
          :display-asterix="false"
          :end-price="offer.listing.price"
          :start-price="{ amount: '0', currency: offer.listing.price.currency }"
        />
      </div>

      <OfferGraph
        :device-name="offer.product.shortTitle"
        :price="offer.listing.price"
        :tiering="offer.tier"
        :variation="
          experiments['experiment.newOfferPage'] === 'withOfferV2'
            ? 'asterix'
            : 'tilde'
        "
      />

      <ReassuranceBanner
        :variation="
          experiments['experiment.newOfferPage'] === 'withOfferV2'
            ? 'standard'
            : 'contrasted'
        "
      />

      <div class="flex flex-col gap-12">
        <h2 class="body-1-bold">{{ i18n(translations.summary) }}</h2>

        <OfferSummary :summary="offer.funnelSummary" />
      </div>
    </div>

    <section
      class="bg-surface-default-low mb-[105px] w-full py-40 md:mb-[70px]"
    >
      <div
        class="mx-auto flex flex-col md:w-[800px] md:px-24 lg:px-40 lg:py-56"
      >
        <FaqBlock />
      </div>
    </section>

    <RevStickyBar position="bottom">
      <div class="flex justify-center">
        <div
          class="mx-auto flex max-w-full grow flex-col lg:max-w-[800px] lg:basis-full"
        >
          <div
            class="flex flex-col-reverse items-center justify-end gap-8 md:flex-row md:gap-28"
          >
            <div class="flex items-center gap-4">
              <IconHeartFilled
                class="text-static-success-hi mood-inverse"
                size="medium"
              />
              <span v-if="offer.tier === 'low'" class="body-2">{{
                i18n(translations.secondLife)
              }}</span>
              <span v-else class="body-2">{{
                i18n(translations.savvySellers)
              }}</span>
            </div>
            <div class="w-full md:w-224">
              <RevButton
                full-width="always"
                variant="primary"
                @click="acceptOffer"
              >
                {{
                  i18n(translations.sellFor, {
                    price: i18n.price(offer.listing.price, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }),
                  })
                }}
              </RevButton>
            </div>
          </div>
        </div>
      </div>
    </RevStickyBar>
  </div>
</template>

<script setup lang="ts">
import { navigateTo, useRoute } from '#imports'
import { computed, onMounted } from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { omit } from '@backmarket/utils/object/omit'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { RevStickyBar } from '@ds/components/StickyBar'
import { IconCross } from '@ds/icons/IconCross'
import { IconHeartFilled } from '@ds/icons/IconHeartFilled'

import { CMS } from '~/scopes/cms/routes-names'

import FaqBlock from '../components/FaqBlock/FaqBlock.vue'
import OfferGraph from '../components/OfferGraph/OfferGraph.vue'
import OfferSummary from '../components/OfferSummary/OfferSummary.vue'
import ReassuranceBanner from '../components/ReassuranceBanner/ReassuranceBanner.vue'
import { useBuybackFunnelOffer } from '../composables/useBuybackFunnelOffer'
import { ROUTE_NAMES } from '../constants'
import AnimatedPrice from '../swap/components/Intro/AnimatedPrice.vue'

import translations from './OfferV2.translations'

const cmsHomePage = {
  name: CMS.BUYBACK,
  params: {
    pageName: 'home',
  },
}

const route = useRoute()
const { offer } = useBuybackFunnelOffer()
const { trackClick } = useTracking()
const i18n = useI18n()
const experiments = useExperiments()

const getOfferPayload = omit(route.query, ['nextStep', 'listingPublicId'])

const nextStep = computed(() => {
  const query = route?.params?.listingId
    ? {
        partner: route.query.partner,
      }
    : {}

  return offer.value?.bankDetailsRequired
    ? {
        name: ROUTE_NAMES.BANK,
        params: route.params,
        query,
      }
    : {
        name: ROUTE_NAMES.ADDRESS,
        params: route.params,
        query,
      }
})

function trackAction(name: string) {
  trackClick({
    zone: 'buyback',
    name,
    value: {
      ...getOfferPayload,
      listing_public_id: route.params.listingId || '',
      estimation: offer.value?.listing.price.amount.toString(),
    },
  })
}

async function acceptOffer() {
  trackAction('accept_offer')
  await navigateTo(nextStep.value)
}

async function declineOffer() {
  trackAction('decline_offer')
  await navigateTo(cmsHomePage)
}

onMounted(async () => {
  if (!offer.value) {
    await navigateTo(cmsHomePage)
  }
})
</script>
